<template>
  <form @submit.prevent="formSubmit">
    <div class="row">
      <div class="col-md-6">
        <FormInput
          :value="fields.email"
          :label="`${$t('global.email')} *`"
          :errors="validation.fields.email.errors"
          :dirty="validation.fields.email.dirty"
          :disabled="true"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="password"
          v-model="fields.password"
          type="password"
          :label="`${$t('global.password')} *`"
          :errors="validation.fields.password.errors"
          :dirty="validation.fields.password.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="first"
          v-model="fields.first"
          :label="`${$t('global.first-name')} *`"
          :errors="validation.fields.first.errors"
          :dirty="validation.fields.first.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="last"
          v-model="fields.last"
          :label="`${$t('global.last-name')} *`"
          :errors="validation.fields.last.errors"
          :dirty="validation.fields.last.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="phone"
          v-model="fields.phone"
          :label="`${$t('global.phone')} *`"
          :errors="validation.fields.phone.errors"
          :dirty="validation.fields.phone.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="company"
          v-model="fields.company"
          :label="`${$t('global.company')}`"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="address"
          v-model="fields.address"
          :label="`${$t('global.address')} *`"
          :errors="validation.fields.address.errors"
          :dirty="validation.fields.address.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="address2"
          v-model="fields.address2"
          :label="$t('global.address2')"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormInput
          name="city"
          v-model="fields.city"
          :label="`${$t('global.city')} *`"
          :errors="validation.fields.city.errors"
          :dirty="validation.fields.city.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6">
        <FormSelect
          name="state"
          v-model="fields.state"
          :label="`${$t('global.state')} *`"
          :errors="validation.fields.state.errors"
          :dirty="validation.fields.state.dirty"
        >
          <option selected="true" value=""> - {{ $t("global.select-state") }} - </option>
          <option :value="state.code" v-for="state in states" :key="state.code">
            {{ state.name }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-6">
        <FormInput
          name="zip"
          v-model="fields.zip"
          :label="`${$t('global.zip')} *`"
          :errors="validation.fields.zip.errors"
          :dirty="validation.fields.zip.dirty"
          :disabled="submitting"
          class="pb-2"
        />
      </div>
      <div class="col-md-6 mb-4">
        <FormSelect
          name="country"
          v-model="fields.country"
          :label="`${$t('global.country')} *`"
          :errors="validation.fields.country.errors"
          :dirty="validation.fields.country.dirty"
        >
          <option selected="true" value=""> - {{ $t("global.select-country") }} - </option>
          <option v-for="country in countries" :value="country.code" :key="country.code">
            {{ country.name }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-12">
        <FormCheckbox
          v-if="gdpr"
          name="marketingEmails"
          v-model="fields.marketingEmails"
          testId="marketing-emails-input"
          :label="$t('global.gdpr-notice')"
          :errors="validation.fields.marketingEmails.errors"
          :dirty="validation.fields.marketingEmails.dirty"
        />
        <FormCheckbox
          name="accept-terms"
          v-model="fields.acceptTerms"
          testId="accept-terms-input"
          :label="`I have read and agree to the <a href='https://www.nexcess.net/policies/terms-of-service' target='_blank'>Terms of Service</a> and acknowledge receipt of the <a href='https://www.nexcess.net/policies/privacy-policy' target='_blank'>Privacy Policy</a>.`"
          :errors="validation.fields.acceptTerms.errors"
          :dirty="validation.fields.acceptTerms.dirty"
        />
        <div class="mt-3" v-if="gdpr">{{ $t("global.terms-of-service-gdpr") }}</div>
      </div>
    </div>
    <div class="text-right my-2">
      {{ `* ${$t("global.required")}` }}
    </div>
    <div class="form-buttons">
      <template v-if="useRecaptcha">
        <template v-if="recaptchaType === 'invisible'">
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading || submitting"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="formSubmit"
            @expired="onExpired"
            class="recaptcha-container"
          >
            <Btn
              variant="primary"
              type="submit"
              :disabled="loading || submitting"
              :loading="loading || submitting"
              data-test="free-trial-start-button"
            >
              {{ $t("global.free-trial-start") }}
            </Btn>
          </vue-recaptcha>
        </template>
        <template v-else>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="onVerify"
            @expired="onExpired"
            class="recaptcha-container"
          ></vue-recaptcha>
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading || submitting"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <Btn
            variant="primary"
            type="submit"
            :disabled="loading || submitting"
            :loading="loading || submitting"
            data-test="free-trial-start-button"
          >
            {{ $t("global.free-trial-start") }}
          </Btn>
        </template>
      </template>
      <Btn
        v-else
        variant="primary"
        type="submit"
        :disabled="loading || submitting"
        :loading="loading || submitting"
        @click="submit"
        data-test="free-trial-start-button"
      >
        {{ $t("global.free-trial-start") }}
      </Btn>

      <!-- <vue-recaptcha
        v-if="useRecaptcha"
        ref="recaptchaContainer"
        :sitekey="recaptchaSiteKey"
        :loadRecaptchaScript="true"
        @verify="onVerify"
        @expired="onExpired"
      >
        <Btn
          variant="primary"
          type="submit"
          :disabled="isLoading()"
          :loading="isLoading()"
          data-test="free-trial-start-button"
        >
          {{ $t("global.free-trial-start") }}
        </Btn>
      </vue-recaptcha>
      <Btn
        v-else
        variant="primary"
        type="submit"
        :disabled="isLoading()"
        :loading="isLoading()"
        @click="submit"
        data-test="free-trial-start-button"
      >
        {{ $t("global.free-trial-start") }}
      </Btn> -->
    </div>
  </form>
</template>

<script>
import { computed, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import { constants } from "@/utils";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import { fromServer } from "@/utils/translations";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { freeTrialSignup as validationRules } from "leatherman-js/utils/validations/forms";
import VueRecaptcha from "vue-recaptcha";

export default {
  name: "FreeTrialSignupForm",
  props: {
    email: String,
    loading: Boolean,
    enableBack: Boolean,
    backAction: Function,
  },
  components: {
    FormInput,
    FormSelect,
    Btn,
    VueRecaptcha,
    FormCheckbox,
  },
  setup(props, { emit, root }) {
    const gdpr = computed(() => store.state.auth.gdpr);
    const submitting = ref(false);
    const isLoading = () => submitting.value || props.loading;
    const errors = ref(null);
    const fields = reactive({
      email: props.email,
      password: null,
      first: null,
      last: null,
      phone: null,
      company: null,
      address: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
      country: null,
      acceptTerms: false,
      recaptchaToken: null,
      marketingEmails: false,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        first: {
          required: root.$t("validation.first-name-required"),
        },
        last: {
          required: root.$t("validation.last-name-required"),
        },
        email: {
          required: root.$t("validation.email-required"),
          regex: root.$t("validation.invalid-email"),
        },
        password: {
          required: root.$t("validation.password-required"),
          password: root.$t("validation.password-too-weak"),
        },
        phone: {
          required: root.$t("validation.phone-required"),
          regex: root.$t("validation.invalid-phone"),
        },
        address: {
          required: root.$t("validation.address-required"),
        },
        city: {
          required: root.$t("validation.city-required"),
        },
        state: {
          required: root.$t("validation.state-required"),
        },
        zip: {
          required: root.$t("validation.zip-required"),
          regex: root.$t("validation.invalid-zip"),
        },
        country: {
          required: root.$t("validation.country-required"),
        },
        acceptTerms: {
          isTruthy: root.$t("validation.accept-terms-equal-true"),
        },
      }
    );

    const countries = Object.keys(constants.COUNTRIES).map((code) => {
      return { code, name: constants.COUNTRIES[code] };
    });

    const states = Object.keys(constants.STATES).map((code) => {
      return { code, name: constants.STATES[code] };
    });

    // recaptcha variables and methods
    //recaptcha variables and methods
    const recaptchaType = ref(process.env.VUE_APP_RECAPTCHA_TYPE);
    const recaptchaSiteKey = ref(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
    const recaptchaContainer = ref(null);
    const useRecaptcha = computed(() => {
      return store.state.settings.loginRecaptchaEnabled;
    });

    const formSubmit = (token) => {
      if (recaptchaType.value === "invisible") {
        onVerify(token);
      }

      submit();
    };

    const onVerify = (token) => {
      fields.recaptchaToken = token;
    };

    const onExpired = () => {
      resetRecaptcha();
    };

    const resetRecaptcha = () => {
      fields.recaptchaToken = null;
      if (recaptchaContainer.value) {
        recaptchaContainer.value.reset();
      }
    };

    const signup = () => {
      submitting.value = true;
      store
        .dispatch("auth/signup", {
          email: fields.email,
          password: fields.password,
          recaptchaToken: fields.recaptchaToken,
          firstName: fields.first,
          lastName: fields.last,
          phone: fields.phone,
          company: fields.company,
          address1: fields.address,
          address2: fields.address2,
          city: fields.city,
          state: fields.state,
          zip: fields.zip,
          country: fields.country,
          classification: props.classification,
          acceptTerms: fields.acceptTerms,
          marketingEmails: fields.marketingEmails,
        })
        .then((user) => {
          emit("success", user);
        })
        .catch((e) => {
          const errors = fromServer(e.request.response);
          if (Array.isArray(errors)) {
            errors.forEach((error) =>
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: error,
                timeout: true,
              })
            );
          } else {
            store.dispatch("toasts/addToast", {
              variant: "error",
              message: errors,
              timeout: true,
            });
          }
          resetRecaptcha();
          emit("error");
        })
        .finally(() => {
          submitting.value = false;
        });
    };

    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          signup();
        })
        .catch(() => {
          resetRecaptcha();
        });
    };
    return {
      recaptchaSiteKey,
      recaptchaContainer,
      resetRecaptcha,
      useRecaptcha,
      onVerify,
      onExpired,
      countries,
      errors,
      fields,
      submit,
      states,
      validation,
      submitting,
      isLoading,
      formSubmit,
      recaptchaType,
      gdpr,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}

.recaptcha-container {
  justify-content: flex-end;
  text-align: right;
}
</style>
